/* ------------------------------------------------------------------
   NOVIUM — engraving window, mobile "typing" state (18 Sep 2026)

   While the keyboard is up, the modal image frame crops from the full
   square down to the engraving zone and the window slides to the top
   of the screen, so the engraving, the name field and the font buttons
   all stay above the keyboard. The image is never scaled — only its
   frame is shorter — so preview calibration is unaffected.

   The state follows the KEYBOARD, not the focus: on Android the field
   keeps focus when the keyboard is dismissed with the system button,
   and no blur fires, so focus alone would leave the image cropped.
   visualViewport tells us the keyboard is really gone.

   Closing is delayed 250ms on purpose: tapping a font button blurs the
   field first, and if the layout grew back at once the button would
   move out from under the finger and the tap would be lost.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var MOBILE      = '(max-width: 767px)';
  var CROP_RATIO  = 165 / 354;   /* cropped frame height, as a share of the image */
  var SHIFT_RATIO = 0;           /* 21 Sep: no top crop — the frame only shortens from the bottom */
  var CLOSE_DELAY = 250;         /* ms — see note above */
  var KB_MARGIN   = 120;         /* px the viewport must lose before we call it a keyboard */
  var SETTLE      = 600;         /* ms after focus in which we ignore "keyboard is down" */

  /* iPhone / iPad Safari (30 Sep). Safari does not resize the page for the keyboard: it slides
     the visual viewport to bring the focused field into view, and it does so AGAIN whenever
     that field moves. The --eng-pan trick below (lower the window by the slide) therefore
     feeds itself there: we lower the window, the field goes down with it, Safari slides
     further, we lower it more — until the window is under the keyboard (seen on Bastien's
     iPhone). So on iOS the window is never moved or scrolled by us at all.
     Safari still slides the view to seat the field above the keyboard, and again while
     typing, which carried the preview off the top (second test, 30 Sep). On iOS that slide
     is now kept in check by focusing the field ourselves after the crop, and by iosSeat()
     (1 Oct, see there). */
  var IOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  /* iOS (1 Oct): Apple's floating key / card / pin bar sits over the "Regular" button and no
     field setting removes it (autocomplete hints, then a search-type field: both still showed
     it). So while the keyboard is up on iPhone, the "Add a Personal Engraving: price" heading
     is hidden (~95px), which lifts the field and the fonts clear of the bar. Back when the
     keyboard closes. Android keeps it. */
  if (IOS) {
    document.documentElement.classList.add('nvm-ios');
    var iosCss = document.createElement('style');
    iosCss.textContent = '.nvm-ios .modal.eng-typing .enghead{display:none}';
    (document.head || document.documentElement).appendChild(iosCss);
  }

  var vv         = window.visualViewport;
  var tallest    = vv ? vv.height : window.innerHeight;   /* running max = no keyboard */
  var closeTimer = null;
  var openedAt   = 0;

  function isMobile()       { return window.matchMedia(MOBILE).matches; }
  /* x scale: a pinch-zoom also shrinks the visual viewport, and must not read as a keyboard */
  function viewportHeight() { return vv ? vv.height * (vv.scale || 1) : window.innerHeight; }
  function keyboardOpen()   { return (tallest - viewportHeight()) > KB_MARGIN; }
  function fieldFrom(node)  { return node && node.closest ? node.closest('.input_text') : null; }
  function focusedField()   { return fieldFrom(document.activeElement); }

  /* The image keeps its full height at all times, so this measures the
     same value whether or not the frame is currently cropped. */
  function measure(modal) {
    var img = modal.querySelector('.enggravpopbanner img');
    if (!img) return false;
    /* offsetHeight, not getBoundingClientRect: the rect includes transforms, so
       it would read the frame's own slide back in. offsetHeight is the layout
       size. Falls back to the width — the renders are square. */
    var h = img.offsetHeight || img.offsetWidth;
    if (!h) return false;
    h = Math.round(h);
    if (modal.__engH === h) return true;      /* unchanged: don't restyle */
    modal.__engH = h;
    /* --eng-full-h matters: a height transition needs a LENGTH at both ends.
       Left at its natural height:auto the frame snapped instead of animating. */
    modal.style.setProperty('--eng-full-h', h + 'px');
    modal.style.setProperty('--eng-crop-h', Math.round(h * CROP_RATIO) + 'px');
    modal.style.setProperty('--eng-crop-y', Math.round(h * SHIFT_RATIO) + 'px');
    return true;
  }

  /* 1 Oct: the preview is no longer PINNED (30sep-d to -i). The pin could not tell Safari's
     slide from the customer's own scroll, so scrolling the window slid the heading under the
     photo. Safari's slide is now taken back by iosSeat() instead, and the window scrolls as
     one piece, as on Android. The window is also no longer shortened while typing (30sep-f):
     it is fixed to the top of the PAGE's frame, so with Safari's view slid 315px only its
     top 194px stayed on screen and the page showed below it (upsell, Shopify bar). */

  /* iOS (1 Oct): while the engraving window is open, the page behind is frozen where it is
     (body fixed at its own scroll), so a drag can no longer move it — Safari ignores
     overscroll-behavior there. On close it goes back to exactly the same scroll. Android is
     not touched: its keyboard handling is tuned against a page that scrolls. */
  var locked = false, lockY = 0, lockWas = null;
  var LOCK_PROPS = ['position', 'top', 'left', 'right', 'width'];
  function lockPage(on) {
    if (!IOS || on === locked) return;
    locked = on;
    var s = document.body.style, i;
    if (on) {
      lockY = window.scrollY;
      lockWas = {};
      for (i = 0; i < LOCK_PROPS.length; i++) lockWas[LOCK_PROPS[i]] = s[LOCK_PROPS[i]];
      s.position = 'fixed'; s.top = -lockY + 'px'; s.left = '0'; s.right = '0'; s.width = '100%';
    } else {
      for (i = 0; i < LOCK_PROPS.length; i++) s[LOCK_PROPS[i]] = lockWas ? lockWas[LOCK_PROPS[i]] : '';
      try { window.scrollTo({ top: lockY, behavior: 'instant' }); } catch (e) { window.scrollTo(0, lockY); }
    }
  }
  function syncLock() { lockPage(!!document.querySelector('.modal.is-visible .enggravpopbanner')); }

  /* Confirm: the theme closes the window and at once measures where #product is, to scroll to
     the "Your engraving" summary. With the page still locked that measure was wrong and the
     customer was not taken there (fifth test). So the page is unlocked FIRST — this capture
     listener runs before the theme's own click handler. If the theme refuses the name (empty,
     bad characters), the window stays open and the lock goes back on straight after. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('.confirmbtn')) return;
    if (locked) { lockPage(false); setTimeout(syncLock, 0); }
    if (isMobile()) setTimeout(centreSummary, 0);
  }, true);

  /* Phones, all (1 Oct): after Confirm, the "Your engraving" summary is scrolled to the MIDDLE
     of the screen. The theme scrolls to the top of #product, which left the summary low on
     the screen (sixth test). Runs after the theme's handler and replaces its scroll. */
  function centreSummary() {
    var box = document.querySelector('.engravingresult');
    if (!box || !box.offsetHeight) return;
    if (document.querySelector('.modal.is-visible .enggravpopbanner')) return;   /* name refused */
    var r = box.getBoundingClientRect();
    var top = Math.max(0, Math.round(window.scrollY + r.top + r.height / 2 - window.innerHeight / 2));
    var $ = window.jQuery;
    if ($) $('html, body').stop(true).animate({ scrollTop: top }, 600);
    else window.scrollTo({ top: top, behavior: 'smooth' });
  }

  /* iOS (1 Oct): Safari scrolls the view when the keyboard opens far more than the field needs
     (fourth test: 301px, field landed at the very top, preview gone above the screen, though
     the field fits 45px above the keyboard). Once the keyboard is up, take back whatever is
     more than "field bottom + 12px above the keyboard". Only with the page locked, so this
     moves nothing but Safari's own slide. Measured against the window's own top, so it holds
     whichever frame iOS reports positions in. */
  function iosSeat() {
    if (!IOS || !locked || !keyboardOpen()) return;
    var modal = document.querySelector('.modal.eng-typing');
    var field = modal && modal.querySelector('.input_text');
    if (!field) return;
    var mTop    = modal.getBoundingClientRect().top;               /* -slide */
    var current = -mTop;
    var need    = Math.max(0, field.getBoundingClientRect().bottom - mTop + 12 - viewportHeight());
    var d = need - current;
    if (Math.abs(d) > 2) window.scrollTo(0, Math.max(0, window.scrollY + d));
  }

  function crop(modal) {
    clearTimeout(closeTimer);
    if (!measure(modal)) return;
    if (IOS) {
      /* no animation on iOS: the layout must be final before the keyboard lands */
      var banner = modal.querySelector('.enggravpopbanner');
      if (banner) {
        banner.style.transition = 'none';
        if (banner.firstElementChild) banner.firstElementChild.style.transition = 'none';
      }
    }
    void modal.offsetHeight;   /* flush, so height animates instead of snapping */
    modal.classList.add('eng-typing');
  }

  function uncrop(modal, delay) {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      modal.classList.remove('eng-typing');
      setPan(modal, 0);
    }, delay);
  }

  /* 21 Sep: put the window back at its gap (the image at the top of what is
     visible), with the field still above the keyboard — whatever the window
     was scrolled to when the field was tapped.

     Done as ONE motion, not a smooth-scroll: the scroll jumps instantly and a
     transform cancels the jump, then the window glides into place over the
     same 420ms as the crop. A smooth-scroll ran as a second animation against
     the crop, and against Chrome's own scrolling, which is what flickered and
     why it only worked some of the time. */
  function visibleBox() {
    /* iOS: measured from the top of the page's own viewport, ignoring Safari's slide, so the
       result never depends on the slide and cannot chase it */
    if (IOS) return { top: 0, bottom: viewportHeight() };
    var top = vv ? vv.offsetTop : 0;
    return { top: top, bottom: top + viewportHeight() };
  }

  /* Current vertical offset of the window's own glide (FLIP), so we can
     measure the layout as it WILL be, not where the animation is right now. */
  function glideY(frame) {
    var t = getComputedStyle(frame).transform;
    if (!t || t === 'none') return 0;
    var m = t.match(/matrix(3d)?\(([^)]+)\)/);
    if (!m) return 0;
    var v = m[2].split(',');
    return parseFloat(m[1] ? v[13] : v[5]) || 0;
  }

  /* 21 Sep (b): Android Chrome sometimes PANS the whole page up when the
     keyboard opens (visualViewport.offsetTop > 0), instead of scrolling the
     window. The window cannot scroll above 0, so that pan hid its top — X
     included. The shortfall is now pushed into --eng-pan, which lowers the
     window by exactly the pan, so it still sits at its gap on screen. */
  function setPan(modal, px) {
    px = Math.max(0, Math.round(px));
    if (modal.__engPan === px) return;
    modal.__engPan = px;
    modal.style.setProperty('--eng-pan', px + 'px');
  }

  function settle(field) {
    if (IOS) return;                       /* hands off on iOS: iosSeat() does it there */
    var scroller = field.closest('.modal');
    if (!scroller) return;
    var frame = scroller.querySelector('.modal-wrapper');
    if (!frame) return;

    /* what is still to come: the frame's shrink, and the glide's remainder */
    var pending = 0;
    if (scroller.classList.contains('eng-typing') && scroller.__engH) {
      var banner = scroller.querySelector('.enggravpopbanner');
      if (banner) pending = Math.max(0, banner.offsetHeight - Math.round(scroller.__engH * CROP_RATIO));
    }
    var ty   = glideY(frame);
    var pan0 = scroller.__engPan || 0;
    var from = scroller.scrollTop;
    var gap  = frame.offsetTop - pan0;                            /* the --eng-gap itself */
    var box  = visibleBox();

    /* settled on-screen positions, then the same with no scroll and no pan */
    var fTop      = frame.getBoundingClientRect().top - ty;
    var baseTop   = fTop + from - pan0;
    var fieldBase = field.getBoundingClientRect().bottom - ty - pending + from - pan0;

    /* window top at its gap: scroll if we can, pan-compensate what we can't */
    var sc = baseTop - (box.top + gap), pn = 0;
    if (sc < 0) { pn = IOS ? 0 : -sc; sc = 0; }
    /* the field wins: you are typing in it */
    var under = (fieldBase - sc + pn) + 12 - box.bottom;
    if (under > 0) {
      if (pn >= under) pn -= under;
      else { sc += under - pn; pn = 0; }
    }

    var newTop = baseTop - sc + pn;
    var shift  = (fTop + ty) - newTop;                            /* keeps it visually still */
    if (Math.abs(newTop - fTop) < 2 && Math.abs(pn - pan0) < 1) return;

    if (IOS) {                 /* no glide on iOS either: one instant move, then hands off */
      scroller.scrollTop = sc;
      setPan(scroller, 0);
      return;
    }
    frame.style.transition = 'none';
    scroller.scrollTop = sc;
    setPan(scroller, pn);
    frame.style.transform = 'translateY(' + shift + 'px)';
    void frame.offsetHeight;
    frame.style.transition = 'transform var(--eng-dur, 420ms) var(--eng-ease, ease)';
    frame.style.transform = '';
    clearTimeout(frame.__engT);
    frame.__engT = setTimeout(function () { frame.style.transition = ''; }, 500);
  }

  /* Once the keyboard is really up, check again: Chrome may have moved things
     when it landed. Debounced — visualViewport fires resize repeatedly. */
  var settleTimer = null;
  function settleSoon() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      var field = focusedField();
      if (field && isMobile()) { if (IOS) iosSeat(); else settle(field); }
    }, 150);
  }

  document.addEventListener('focusin', function (event) {
    var field = fieldFrom(event.target);
    if (!field || !isMobile()) return;
    var modal = field.closest('.modal');
    if (!modal) return;

    openedAt = Date.now();
    var wasCropped = modal.classList.contains('eng-typing');
    crop(modal);
    if (!wasCropped && modal.__engH) settle(field);
  });

  /* iOS (1 Oct): Safari picks its scroll at the moment of focus, when the photo is still full
     size and the field low — so it scrolled ~300px, and iosSeat() scrolling back read as a
     jump up and down. Instead the tap on the field is taken over: on finger-up the photo is
     cropped at once, THEN the field is focused by us, already high enough above the keyboard
     for Safari to leave the view alone. (30sep-c cropped on touchstart and the native tap
     missed the moved field; here the focus is ours, so it cannot miss.) A drag that starts
     on the field is left alone. */
  var tapX = 0, tapY = 0;
  document.addEventListener('touchstart', function (e) {
    if (e.touches.length === 1) { tapX = e.touches[0].clientX; tapY = e.touches[0].clientY; }
  }, { capture: true, passive: true });
  document.addEventListener('touchend', function (e) {
    if (!IOS || !isMobile() || e.changedTouches.length !== 1) return;
    var input = e.target && e.target.closest ? e.target.closest('.input_text') : null;
    if (!input) return;
    var modal = input.closest('.modal');
    if (!modal) return;
    var t = e.changedTouches[0];
    if (Math.abs(t.clientX - tapX) > 10 || Math.abs(t.clientY - tapY) > 10) return;   /* a drag */
    /* keyboard closed but the field kept focus (seventh test: the second tap went to Safari
       uncropped and the jump came back): drop the focus and take the tap over like a first one */
    if (document.activeElement === input && !modal.classList.contains('eng-typing')) input.blur();
    if (document.activeElement === input) {
      /* already typing (sixth test: keyboard up, window scrolled down, tap on the field left
         the photo out of sight). Photo in view: native, so the caret goes where tapped. */
      if (modal.scrollTop < 10) return;
      e.preventDefault();
      modal.scrollTop = 0;
      return;
    }
    e.preventDefault();
    openedAt = Date.now();
    /* the window back at its top, photo in view — what settle() does on Android (fifth test:
       tapping the field after scrolling down in the window left the photo out of sight) */
    modal.scrollTop = 0;
    crop(modal);
    void modal.offsetHeight;                                     /* layout final before focus */
    try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
  }, { capture: true, passive: false });

  document.addEventListener('focusout', function (event) {
    var field = fieldFrom(event.target);
    if (!field) return;
    var modal = field.closest('.modal');
    if (modal) uncrop(modal, CLOSE_DELAY);
  });

  /* Keyboard opened or closed, screen rotated, or we left mobile. */
  function onViewportChange() {
    var h = viewportHeight();
    if (h > tallest) tallest = h;

    var cropped = document.querySelectorAll('.modal.eng-typing');
    var i;

    if (!isMobile()) {
      for (i = 0; i < cropped.length; i++) cropped[i].classList.remove('eng-typing');
      return;
    }

    if (keyboardOpen()) {
      var field = focusedField();
      if (field) {
        var modal = field.closest('.modal');
        if (modal && !modal.classList.contains('eng-typing')) crop(modal);
      }
      for (i = 0; i < cropped.length; i++) measure(cropped[i]);   /* rotation; no-op if unchanged */
      settleSoon();
      return;
    }

    /* Keyboard is down — including dismissed with the field still focused.
       Ignore it right after focus, before the keyboard has had time to open. */
    if (Date.now() - openedAt < SETTLE) return;
    for (i = 0; i < cropped.length; i++) { cropped[i].classList.remove('eng-typing'); setPan(cropped[i], 0); }
  }

  /* Publish the resting height early, so the very first crop animates too. */
  function prime() {
    var modals = document.querySelectorAll('.modal');
    for (var i = 0; i < modals.length; i++) {
      if (!modals[i].querySelector('.enggravpopbanner img')) continue;
      measure(modals[i]);
      /* a drag that reaches the end of the window must not carry on into the page behind */
      modals[i].style.overscrollBehavior = 'contain';
      /* as the window opens and closes: iOS locks / unlocks the page; all devices open the
         window at its TOP. It is only hidden on close, so it kept its scroll and "Edit" after
         Confirm reopened it half-way down (1 Oct, iPhone and Android). */
      if (!modals[i].__nvmLockObs && window.MutationObserver) {
        modals[i].__nvmLockObs = new MutationObserver(function (list) {
          var m = list[0].target, open = m.classList.contains('is-visible');
          if (open && !m.__nvmOpen) m.scrollTop = 0;
          m.__nvmOpen = open;
          syncLock();
        });
        modals[i].__nvmOpen = modals[i].classList.contains('is-visible');
        modals[i].__nvmLockObs.observe(modals[i], { attributes: true, attributeFilter: ['class'] });
      }
      /* a name to engrave is not a login, a card or an address: ask the browser and the
         password managers not to offer autofill on it (Safari's floating key / card / pin
         bar covered the window on iPhone). A request — the browser has the last word. */
      var f = modals[i].querySelector('.input_text');
      if (f && !f.__nvmNoFill) {
        f.__nvmNoFill = true;
        f.setAttribute('autocomplete', 'off');
        f.setAttribute('autocorrect', 'off');
        f.setAttribute('spellcheck', 'false');
        f.setAttribute('data-1p-ignore', '');
        f.setAttribute('data-lpignore', 'true');
        f.setAttribute('data-form-type', 'other');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prime);
  } else {
    prime();
  }
  window.addEventListener('load', prime);      /* once the render has decoded */
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.modal-toggle-size')) setTimeout(prime, 50);
  });

  window.addEventListener('resize', function () { prime(); onViewportChange(); });
  if (vv) {
    vv.addEventListener('resize', onViewportChange);
    vv.addEventListener('scroll', function () { if (document.querySelector('.modal.eng-typing')) settleSoon(); });
  }

  /* Opt-in readout for testing on a phone: add ?nvmkb=1 to the page URL. It sits at the TOP
     of what is visible (the bottom is behind the keyboard on iPhone). */
  if (/[?&]nvmkb=1/.test(location.search)) {
    var dbg = document.createElement('div');
    dbg.style.cssText = 'position:fixed;left:4px;top:4px;z-index:2147483647;background:rgba(0,0,0,.82);' +
      'color:#0f0;font:11px/1.35 monospace;padding:4px 6px;border-radius:3px;pointer-events:none;white-space:pre';
    document.addEventListener('DOMContentLoaded', function () { document.body.appendChild(dbg); });
    if (document.body) document.body.appendChild(dbg);
    setInterval(function () {
      var m = document.querySelector('.modal.is-visible') || document.querySelector('.modal');
      var w = m && m.querySelector('.modal-wrapper'), f = m && m.querySelector('.input_text');
      var b = m && m.querySelector('.enggravpopbanner');
      dbg.style.top = ((vv ? vv.offsetTop : 0) + 4) + 'px';
      dbg.textContent =
        'build 19oct-e  ios ' + (IOS ? 'y' : 'n') + '  win ' + window.innerWidth + 'x' + window.innerHeight +
        '\nvv h ' + Math.round(vv ? vv.height : 0) + '  top ' + Math.round(vv ? vv.offsetTop : 0) +
        '  scale ' + (vv ? (+vv.scale).toFixed(2) : '-') + '  max ' + Math.round(tallest) +
        '\npageY ' + Math.round(window.scrollY) + '  scroll ' + (m ? Math.round(m.scrollTop) : '-') + '  pan ' + (m ? (m.__engPan || 0) : '-') +
        '  typing ' + (m && m.classList.contains('eng-typing') ? 'y' : 'n') + '  lock ' + (locked ? 'y' : 'n') +
        '\nwin top ' + (w ? Math.round(w.getBoundingClientRect().top) : '-') +
        '  img h ' + (b ? Math.round(b.offsetHeight) : '-') +
        '  field bot ' + (f ? Math.round(f.getBoundingClientRect().bottom) : '-') +
        '  kb ' + (keyboardOpen() ? 'up' : 'down') +
        '\nbox h ' + (m ? m.clientHeight : '-') + '  content ' + (m ? m.scrollHeight : '-') +
        '  ban top ' + (b ? Math.round(b.getBoundingClientRect().top) : '-');
    }, 150);
  }
})();

/* ==========================================================================
   ENGRAVING PREVIEW OVERLAY — Interstellar (30 Sep), Future and Classic (2 Oct)
   Swaps the popup image for the engraving-card render of the SELECTED colour
   (text-free) and paints the typed name as SVG text in TWO places — on the
   enlarged barrel in the card, and on the pen's own shaft — plus the preview
   label from the translatable "Preview label" setting.
   Interstellar: cards by build_card.py from the 1500 px originals, pen at 70% or
   80% in the card (section setting "Engraving card size").
   Future / Classic: cards by future_render.py / classic_render.py (future-02oct/),
   the name filling the 2.8 mm engraving zone; Future 2.1x the main pen, Classic
   1.75x. One size each, no setting.

   The card images live in Content > Files (engraving-card-<size>-<colour>.jpg,
   engraving-card-future-<colour>.jpg, engraving-card-classic-<colour>.jpg),
   NOT in the theme's Assets (30 Sep: that is where they were uploaded). Their URLs
   reach this file through data-nvm-cards on .enggravpopbanner, written by
   cw-main-product.liquid, with data-nvm-pen = future / classic (none = Interstellar).
   No data-nvm-cards = this block does nothing and the popup keeps its normal
   image (the carrying case, any other product).

   The engine is the live-preview prototype's (live-preview-block.liquid),
   unchanged in behaviour:
   - the engraving fonts load under PRIVATE names from the exact files the
     theme ships, so no system or theme "Avenir" can win, and nothing is
     synthesised (both at weight 400 / normal: the files already are the
     Medium cut and the italic)
   - fonts are set as inline !important styles: presentation attributes
     lose to the theme's global font rules and fall back to the body font
   - font-size is derived from the ACTUAL string's ink height, descenders
     included, so every name fills the same band
   - a name is never squeezed or shrunk to fit its length (see CARD_LEFT below)

   Regions, in 1080 units, one set per pen pose (GROUPS below):
   card   the enlarged shaft in the card. build_card.py puts the tip in the
          same place for every colour, so every group starts from the same
          card values (Space Black, calibrated on the theme 30 Sep).
   shaft  the pen itself. Space Black is calibrated; the other groups are
          Space Black's values moved by the measured difference in tip
          position, axis angle and shaft thickness — to be checked by eye.

   cw-script.js swaps the image src on every font or colour change, so an
   observer puts the right card back each time.
   Calibration tool: ?nvmdebug=1 (see CALIBRATION TOOL below).
   DELETE THIS WHOLE BLOCK (and section 1b in engrave.css) to return to the
   normal popup image.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = {
    calibrationFor: 'black',           /* the pose group in use; set by the selected colour */
    viewBox: 1080,
    fill: '#f2f2f2',
    opacity: 0.95,
    regions: null,                     /* = GROUPS[group].regions, set by the selected colour */
    fontFiles: {
      'Novium Engrave':
        'https://cdn.shopify.com/s/files/1/0596/3150/1405/files/AvenirNextCondensed-Medium.woff2?v=1768888606',
      'Novium Engrave Script':
        'https://cdn.shopify.com/s/files/1/0596/3150/1405/files/RageItalicExt-Hook.ttf?v=1792029357',   /* the theme's Rage (engrave.css, 15 Oct); Shopify serves it as WOFF2 */
      /* Future is engraved in the Regular cut (Ludovic, 6 Oct); only downloaded when used */
      'Novium Engrave Regular':
        'https://cdn.shopify.com/s/files/1/0596/3150/1405/files/AvenirNextCondensed-Regular.woff2'
    },
    heartsFile: 'https://cdn.shopify.com/s/files/1/0596/3150/1405/files/NoviumHearts-v2.woff2',
    fonts: {
      regular: "'Novium Engrave', 'Avenir Next Condensed', sans-serif",
      regularFuture: "'Novium Engrave Regular', 'Novium Engrave', 'Avenir Next Condensed', sans-serif",
      /* a letter Rage does not have (Polish Ł, Ó…) falls back to the STANDARD engraving font,
         as Guillaume describes it, not to the phone's own cursive (1 Oct) */
      cursive: "'Novium Engrave Script', 'Rage', 'Novium Engrave', 'Avenir Next Condensed', sans-serif"
    },
    label: { x: 80, y: 307, size: 26, spacing: 4.2, fill: '#8A8E9C',
             family: "'Courier New', Courier, monospace" }
  };
  var KEYS = ['card', 'shaft'];

  /* One entry per pen pose. fill = the engraving colour on that pen. */
  var LIGHT = '#f2f2f2', DARK = '#51504D';       /* DARK: the name on the silver pen */
  /* The name on the card, per card size (the pen's size in the card, % of the 21 Sep layout).
     Ludovic's calibration on the theme: 70 first (30 Sep), then the name moved 4 units further
     from the tip at 80 (30 Sep, second pass), carried back to 70 in proportion. 80 is otherwise the same placement carried over
     by measurement: same distance from the tip and from the barrel's centre line, in barrel
     widths. The cards are built so the pen sits in the same place for every colour
     (build_card_aligned.py), so one entry serves all of them. */
  var CARD_AT = {
    70: { capX: 658.3, capY: 192.5, barrelPx: 42.7 },
    80: { capX: 658.1, capY: 192.9, barrelPx: 48.8 }
  };
  var SCALE = 70;                                  /* set from data-nvm-scale in build() */
  function card() {
    var c = CARD_AT[SCALE] || CARD_AT[70];
    return { capX: c.capX, capY: c.capY, rotate: 0, barrelPx: c.barrelPx, endGap: 1.88, height: 0.896, lift: 0.07 };   /* 6 Oct: x1.12, real pens (Lucas Hart); end point as the real pen (endGap 1.40 -> 1.88) */
  }
  /* 6 Oct: card and pen were calibrated separately in Sept and disagreed: the card is a 2.2x crop of
     the same render, but its letters were 1.27x bigger than the pen's (relative to the barrel) and
     its name ended closer to the tip. Now one engraving: pen letters 0.594 -> 0.753 (the card's size,
     which matches the real pens); both end points moved to the real pen's gap (~1.55 tip thicknesses, 'Lucas Hart'):
     card endGap 1.40 -> 1.88, pen 1.40 -> 1.61. */
  var GROUPS = {
    /* Space Black + 18K — calibrated by Ludovic on the theme, 30 Sep (second pass: further from the tip) */
    black:   { fill: LIGHT, regions: { card: card(),
               shaft: { capX: 888.5, capY: 111.2, rotate: -64.1, barrelPx: 20.7, endGap: 1.61, height: 0.753, lift: 0.07 } } },
    /* the rest: derived from Space Black by the renders' measured pose, same distance from the tip
       in barrel widths — check with ?nvmdebug=1 */
    silver:  { fill: DARK,  regions: { card: card(),
               shaft: { capX: 885.3, capY: 120.2, rotate: -64.1, barrelPx: 23.1, endGap: 1.61, height: 0.753, lift: 0.07 } } },
    red:     { fill: LIGHT, regions: { card: card(),
               shaft: { capX: 888.1, capY: 92.2,  rotate: -64.9, barrelPx: 23.1, endGap: 1.61, height: 0.753, lift: 0.07 } } },
    blue:    { fill: LIGHT, regions: { card: card(),
               shaft: { capX: 896.0, capY: 105.1, rotate: -64.0, barrelPx: 23.1, endGap: 1.61, height: 0.753, lift: 0.07 } } },
    blue18k: { fill: LIGHT, regions: { card: card(),
               shaft: { capX: 893.2, capY: 107.1, rotate: -64.0, barrelPx: 22.3, endGap: 1.61, height: 0.753, lift: 0.07 } } }
  };
  /* colour -> [card image, its 18K card image, pose group, 18K pose group].
     The colour is read from the swatch's class (the option value without spaces)
     and, failing that, from its position in the list (data-num). */
  var COLOURS = [
    { cls: 'SpaceBlack',      std: 'space-black',      gold: 'space-black-18k',      group: 'black',  goldGroup: 'black'   },
    { cls: 'StarlightSilver', std: 'starlight-silver', gold: 'starlight-silver-18k', group: 'silver', goldGroup: 'silver'  },
    { cls: 'MarsMagma',       std: 'mars-magma',       gold: 'mars-magma-18k',       group: 'red',    goldGroup: 'red'     },
    { cls: 'NeptuneBlue',     std: 'neptune-blue',     gold: 'neptune-blue-18k',     group: 'blue',   goldGroup: 'blue18k' }
  ];

  /* Future and Classic (2 Oct). Starting values computed from the scripts that built the
     cards (future_render.py / classic_render.py), not yet checked on the theme: calibrate
     with ?nvmdebug=1. Same units as above; anchor 'start' = endGap measures, back from the
     cap tip, to where the name BEGINS (Future: the name starts after the ring and grows
     toward the cap). height = Ludovic's 2.8 mm zone in barrel widths, lift 0 = centred. */
  var FUTURE_GROUPS = {
    /* Deep Black + 18K: pen at 31 deg */
    black:      { fill: LIGHT, regions: {
      card:  { capX: 886.2, capY: 190,   rotate: 0,   barrelPx: 141.2, endGap: 5.164, height: 0.27,  lift: 0, anchor: 'start' },
      shaft: { capX: 992.1, capY: 319.4, rotate: -31, barrelPx: 67.3,  endGap: 5.164, height: 0.27,  lift: 0, anchor: 'start' } } },   /* calibrated by Ludovic 6 Oct */
    /* Frost Silver, Mist Blue + 18K: same render pose, 27 deg (silver's dark name: COLOURS below) */
    silverblue: { fill: LIGHT, regions: {
      card:  { capX: 885.6, capY: 189.5, rotate: 0,   barrelPx: 140.1, endGap: 5.164, height: 0.27,  lift: 0, anchor: 'start' },
      shaft: { capX: 989.6, capY: 364.1, rotate: -27, barrelPx: 66.7,  endGap: 5.164, height: 0.27,  lift: 0, anchor: 'start' } } }   /* calibrated by Ludovic 6 Oct (height as black) */
  };
  var FUTURE_COLOURS = [
    { cls: 'DeepBlack',   std: 'deep-black',   gold: 'deep-black-18k', group: 'black',      goldGroup: 'black' },
    { cls: 'FrostSilver', std: 'frost-silver', gold: '',               group: 'silverblue', goldGroup: 'silverblue', fill: DARK },
    { cls: 'MistBlue',    std: 'mist-blue',    gold: 'mist-blue-18k',  group: 'silverblue', goldGroup: 'silverblue' }
  ];
  /* Classic: one upright pose for all three colours (the renders differ by ~1.5% in width) */
  /* 6 Oct: GTP confirmed the rule: whatever the text, its own ink box (tails included) is 2.8 mm
     tall, right-aligned in a 25 x 2.8 mm box; longer text runs on to the left. That is this
     engine's rule. GTP's 2.8 mm test pens show the barrels are ~9.6 mm thick, not the 6.3 / 7.5
     first estimated from the drawings (name length / letter height in the photos = the font's own
     ratio, so the letters there are 2.8 mm): Classic 0.446 -> 0.29, Future 0.351 -> 0.27 (real pen next to
     the screen, 'Create the future', Rage). */
  var CLASSIC_GROUPS = {
    upright: { fill: LIGHT, regions: {
      card:  { capX: 751,   capY: 190, rotate: 0,   barrelPx: 99.1, endGap: 0.807, height: 0.29,  lift: 0 },
      shaft: { capX: 820.9, capY: 174.5, rotate: -90, barrelPx: 56.8, endGap: 0.807, height: 0.29,  lift: 0 } } }   /* calibrated by Ludovic 6 Oct */
  };
  var CLASSIC_COLOURS = [
    { cls: 'DarkGrey',     std: 'dark-grey',     gold: '', group: 'upright', goldGroup: 'upright' },
    { cls: 'PearlGrey',    std: 'pearl-grey',    gold: '', group: 'upright', goldGroup: 'upright', fill: DARK },
    { cls: 'MidnightBlue', std: 'midnight-blue', gold: '', group: 'upright', goldGroup: 'upright' }
  ];

  /* Per pen: its groups and colours, where the white rectangle is (mask: x, width, the left
     fade, and Future's right fade, where its pen strip runs out), and which picker holds the
     premium option. Interstellar's entry is exactly the 30 Sep behaviour. */
  var PENS = {
    interstellar: { groups: GROUPS, colours: COLOURS, mask: { x: 43, w: 667, fadeL: [52, 95] },
                    premium: '.variant-picker__option:not(.maincolorvar) .blockbox.active' },
    /* Future: Pen Body / Colour / Premium, so the premium is the option AFTER the colour */
    future:       { groups: FUTURE_GROUPS, colours: FUTURE_COLOURS,
                    mask: { x: 43, w: 757, fadeL: [52, 95], fadeR: [748, 791] },
                    premium: '.maincolorvar ~ .variant-picker__option .blockbox.active' },
    classic:      { groups: CLASSIC_GROUPS, colours: CLASSIC_COLOURS, mask: { x: 43, w: 727, fadeL: [52, 95] },
                    premium: null }
  };
  var PEN = 'interstellar';                        /* set from data-nvm-pen in build() */
  CFG.regions = GROUPS.black.regions;
  var MAXLEN = 18;
  /* The laser never squeezes or shrinks a name to make it fit: its only limit is the letter
     height, so a long name just runs longer (Ludovic, 30 Sep). The preview does the same in
     both places. The card shows only part of the barrel, so a name too long for it fades out
     with the pen at the card's left edge. */
  var CARD_LEFT = 95;                     /* 1080 units: where the pen's own fade ends in the card */
  /* The name shown is the one the THEME has cleaned: its grey box (.viewinput_text) receives the
     typed name with Guillaume's character rule applied and cut at 18, on every keystroke.
     Reading it there means the preview follows his rule, whatever he changes (1 Oct: he opened
     Central European letters, À-ſ; this overlay still had the old À-ÿ and previewed "Łukasz"
     as "ukasz"). Where there is no grey box, the theme's own rule is used: filterRegex, built in
     cw-script.js from ENGRAVE_LETTERS / ENGRAVE_SYMBOLS, the ONLY place the characters are listed
     (15 Oct). No copy here. */
  function themeFilter() {
    return (window.filterRegex instanceof RegExp) ? new RegExp(window.filterRegex.source, 'g') : null;
  }
  var NS = 'http://www.w3.org/2000/svg';
  var CAP_FALLBACK = 0.72;
  var capCache = {}, probe = null;

  function el(tag, attrs) {
    var n = document.createElementNS(NS, tag);
    for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }
  function set(node, prop, value) { node.style.setProperty(prop, value, 'important'); }

  function injectFonts() {
    var css = '';
    Object.keys(CFG.fontFiles).forEach(function (family) {
      css += "@font-face{font-family:'" + family + "';src:url('" + CFG.fontFiles[family] +
             "') format('woff2');font-weight:400;font-style:normal;font-display:swap}";
    });
    /* ♥ ♡: the theme's NoviumHearts (engrave.css), so the pen never shows a red emoji heart */
    Object.keys(CFG.fontFiles).forEach(function (family) {
      css += "@font-face{font-family:'" + family + "';src:url('" + CFG.heartsFile +
             "') format('woff2');font-weight:400;font-style:normal;unicode-range:U+2661,U+2665}";
    });
    var tag = document.createElement('style');
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  /* per-string ink height: a name with a descender is set smaller so the
     total ink fills the same band as one without */
  function inkMetrics(family, text) {
    var key = family + '|' + text;
    if (capCache[key]) return capCache[key];
    try { probe = probe || document.createElement('canvas').getContext('2d'); } catch (e) {}
    if (!probe) return { asc: CAP_FALLBACK, desc: 0 };
    probe.font = 'normal 400 100px ' + family;
    var m = probe.measureText(text);
    var asc = m && m.actualBoundingBoxAscent, desc = m && m.actualBoundingBoxDescent;
    if (typeof asc !== 'number' || !asc) return { asc: CAP_FALLBACK, desc: 0 };
    var r = { asc: asc / 100, desc: Math.max(0, (desc || 0) / 100) };
    if (r.asc + r.desc < 0.2 || r.asc + r.desc > 2) return { asc: CAP_FALLBACK, desc: 0 };
    return (capCache[key] = r);
  }

  function geom(key, family, text) {
    var r = CFG.regions[key];
    var rad = r.rotate * Math.PI / 180, cos = Math.cos(rad), sin = Math.sin(rad);
    var band = r.barrelPx * r.height;
    var ink = inkMetrics(family, text || 'H');
    var size = band / (ink.asc + ink.desc);
    var d = r.barrelPx * r.endGap;                     /* right edge, 'end' anchor */
    var perp = (ink.asc - ink.desc) * size / 2 - r.lift * r.barrelPx;
    return {
      x: r.capX - d * cos + (-sin) * perp,
      y: r.capY - d * sin + ( cos) * perp,
      size: size, rotate: r.rotate
    };
  }

  function build() {
    if (!document.querySelector('.productmainleft')) return;       /* Hoverpen template only */
    var banner = document.querySelector('.enggravpopbanner');
    if (!banner || banner.__nvmOv) return;
    var img = banner.querySelector('img');
    var modal = banner.closest('.modal');
    var field = modal && modal.querySelector('.input_text');
    if (!img || !field) return;
    var URLS = null;                                   /* card name -> asset URL, from the section */
    try { URLS = JSON.parse(banner.getAttribute('data-nvm-cards') || 'null'); } catch (e) {}
    PEN = banner.getAttribute('data-nvm-pen') || 'interstellar';
    if (!PENS[PEN]) return;
    GROUPS = PENS[PEN].groups;
    COLOURS = PENS[PEN].colours;
    var FIRST = COLOURS[0].std;                        /* the card shown when nothing else matches */
    if (!URLS || !URLS[FIRST]) return;                 /* no cards for this product: leave the popup alone */
    if (PEN === 'interstellar') {
      var sc = parseInt(banner.getAttribute('data-nvm-scale'), 10);
      if (CARD_AT[sc]) SCALE = sc;
      Object.keys(GROUPS).forEach(function (g) { GROUPS[g].regions.card = card(); });
    }
    CFG.regions = GROUPS[COLOURS[0].group].regions;
    banner.__nvmOv = true;

    injectFonts();

    /* image + text in one wrapper: it is the banner's direct child, so the
       mobile crop's slide moves both together */
    var wrap = document.createElement('div');
    wrap.className = 'nvm-ov';
    img.parentNode.insertBefore(wrap, img);
    wrap.appendChild(img);

    var svg = el('svg', { viewBox: '0 0 ' + CFG.viewBox + ' ' + CFG.viewBox,
                          'class': 'nvm-ov-svg', 'aria-hidden': 'true', preserveAspectRatio: 'none' });
    var nodes = {};
    /* the card's name fades out where the pen does, and never leaves the card */
    var defs = el('defs', {});
    var M = PENS[PEN].mask;
    var x1 = M.fadeL[0], x2 = M.fadeR ? M.fadeR[1] : M.fadeL[1];
    var fade = el('linearGradient', { id: 'nvm-ov-fade', gradientUnits: 'userSpaceOnUse', x1: x1, y1: 0, x2: x2, y2: 0 });
    var stops = [[0, 0], [(M.fadeL[1] - x1) / (x2 - x1), 1]];
    if (M.fadeR) stops.push([(M.fadeR[0] - x1) / (x2 - x1), 1], [1, 0]);   /* Future: fades on the right too */
    stops.forEach(function (s) { fade.appendChild(el('stop', { offset: s[0], 'stop-color': '#fff', 'stop-opacity': s[1] })); });
    var cmask = el('mask', { id: 'nvm-ov-cardmask', maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: CFG.viewBox, height: CFG.viewBox });
    cmask.appendChild(el('rect', { x: M.x, y: 66, width: M.w, height: 272, fill: 'url(#nvm-ov-fade)' }));
    defs.appendChild(fade); defs.appendChild(cmask);
    svg.appendChild(defs);
    var cardLayer = el('g', { mask: 'url(#nvm-ov-cardmask)' });
    svg.appendChild(cardLayer);
    KEYS.forEach(function (key) {
      nodes[key] = el('text', { 'class': 'nvm-ov-name nvm-ov-' + key, 'text-anchor': 'end' });
      (key === 'card' ? cardLayer : svg).appendChild(nodes[key]);
    });

    var L = CFG.label;
    var label = el('text', { 'class': 'nvm-ov-label', x: L.x, y: L.y });
    set(label, 'font-family', L.family);
    set(label, 'font-size', L.size + 'px');
    set(label, 'letter-spacing', L.spacing + 'px');
    set(label, 'fill', L.fill);
    var labelText = (banner.getAttribute('data-preview-label') || '').trim();
    label.textContent = labelText;
    if (!labelText) label.setAttribute('display', 'none');         /* empty setting = hidden */
    svg.appendChild(label);
    wrap.appendChild(svg);

    /* which card the window's own colour / premium pickers point at */
    function selected() {
      var col = modal.querySelector('.maincolorvar .blockbox.active') ||
                modal.querySelector('.popvariant1 .blockbox.active');
      var prem = PENS[PEN].premium ? modal.querySelector(PENS[PEN].premium) : null;   /* Classic: none */
      var c = null;
      if (col) {
        COLOURS.forEach(function (x) { if (col.classList.contains(x.cls)) c = x; });
        if (!c) c = COLOURS[(parseInt(col.getAttribute('data-num'), 10) || 1) - 1];
      }
      c = c || COLOURS[0];
      /* premium option 1 = Standard; 2 and 3 = 18K gold and its gift box (Future: 2 = 18K nib) */
      var gold = !!prem && (parseInt(prem.getAttribute('data-num'), 10) || 1) > 1;
      var name = gold && URLS[c.gold] ? c.gold : c.std;
      return { src: URLS[name] || URLS[FIRST], group: gold && URLS[c.gold] ? c.goldGroup : c.group, fill: c.fill };
    }
    function useGroup(g, fill) {
      CFG.calibrationFor = g;
      CFG.regions = GROUPS[g].regions;
      CFG.fill = fill || GROUPS[g].fill;               /* a colour's own fill wins (Future silver, Classic pearl) */
    }
    /* The name's colour and position belong to the pen ON SCREEN, so the card and the
       name always change in the same instant. A card that is not downloaded yet is
       fetched off-screen first; until it is ready the current card stays, name and all.
       (Waiting for the <img>'s own load event is not enough: a card the browser already
       has is swapped at once, but its load event comes later, behind the theme's work,
       and the name lagged behind the pen — seen on the theme 30 Sep.) */
    var cache = {};
    function fetchCard(url) {
      if (!cache[url]) { cache[url] = new Image(); cache[url].src = url; }
      return cache[url];
    }
    var shown = null;                                  /* the card on screen: { src, group } */
    function show(s) {
      shown = s;
      useGroup(s.group, s.fill);
      if (img.getAttribute('src') !== s.src) img.setAttribute('src', s.src);
      img.removeAttribute('srcset');
      (CFG.onChange || paint)();
    }
    function pin() {
      var s = selected();
      if (!shown) return show(s);
      /* the theme puts its own image in on every change: put the current card back */
      if (img.getAttribute('src') !== shown.src) img.setAttribute('src', shown.src);
      img.removeAttribute('srcset');
      if (s.src === shown.src) return;
      var next = fetchCard(s.src);
      if (next.complete && next.naturalWidth) return show(s);
      if (next.__nvmWait) return;
      next.__nvmWait = true;
      var ready = function () {
        next.__nvmWait = false;
        next.removeEventListener('load', ready); next.removeEventListener('error', ready);
        var now = selected();
        if (now.src === s.src) show(now); else pin();  /* another colour was picked meanwhile */
      };
      next.addEventListener('load', ready);
      next.addEventListener('error', ready);
    }
    /* fetch the other cards once the window is in use, so a colour switch is instant */
    var preloaded = false;
    function preload() {
      if (preloaded) return;
      preloaded = true;
      Object.keys(URLS).forEach(function (k) { fetchCard(URLS[k]); });
    }
    modal.addEventListener('pointerdown', preload, true);
    modal.addEventListener('focusin', preload, true);
    pin();
    new MutationObserver(pin).observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });

    function paint() {
      var box = modal.querySelector('.viewinput_text');
      var txt = box ? box.value : (themeFilter() ? field.value.replace(themeFilter(), '') : field.value).substring(0, MAXLEN);
      var li = modal.querySelector('.engravfontvariant li.active');
      /* same ink-height sizing for every cut, so the calibrated letter height holds in Regular too */
      var family = CFG.fonts[(li && li.classList.contains('cursivefont')) ? 'cursive' :
                             PEN === 'future' ? 'regularFuture' : 'regular'];

      KEYS.forEach(function (key) {
        var g = geom(key, family, txt), t = nodes[key];
        t.textContent = txt;
        t.setAttribute('x', g.x);
        t.setAttribute('y', g.y);
        t.setAttribute('transform', 'rotate(' + g.rotate + ' ' + g.x + ' ' + g.y + ')');
        var anchor = CFG.regions[key].anchor === 'start' ? 'start' : 'end';   /* Future: from the ring on */
        t.setAttribute('text-anchor', anchor);
        set(t, 'text-anchor', anchor);
        set(t, 'font-family', family);
        set(t, 'font-weight', '400');
        set(t, 'font-style', 'normal');
        set(t, 'font-size', g.size + 'px');
        set(t, 'letter-spacing', '0px');
        set(t, 'fill', CFG.fill);
        set(t, 'opacity', txt ? CFG.opacity : 0);
        t.removeAttribute('textLength');
        t.removeAttribute('lengthAdjust');
      });

      fieldFamily = family;
      fieldCursive = !!(li && li.classList.contains('cursivefont'));
      styleField();
    }

    /* The text field (option D, 19 Oct): while the customer is typing, the theme's clean font,
       so a typo is easy to spot. As soon as they are done (they tap elsewhere, or the phone's
       keyboard closes), the name switches to the engraving font, the same as on the pen, so
       every letter can be checked. Sizes as the theme's grey box (Avenir 16px, Rage 20px; never
       under 16px, or iPhone zooms on the field), in black (the theme's grey is too faint for
       Rage). Empty field: the theme's font, so the placeholder is unchanged. */
    var fieldFamily = null, fieldCursive = false;
    var kbClosed = false;   /* Android: the keyboard can close while the field keeps the focus */
    function styleField() {
      var typing = document.activeElement === field && !kbClosed;
      if (field.value && !typing && fieldFamily) {
        set(field, 'font-family', fieldFamily);
        set(field, 'font-weight', '400');
        set(field, 'font-style', 'normal');
        set(field, 'font-size', fieldCursive ? '20px' : '16px');
        set(field, 'color', '#000');
      } else {
        ['font-family', 'font-weight', 'font-style', 'font-size', 'color'].forEach(function (p) { field.style.removeProperty(p); });
      }
    }
    field.addEventListener('focus', function () { kbClosed = false; styleField(); });
    field.addEventListener('blur', function () { kbClosed = false; styleField(); });
    field.addEventListener('input', function () { kbClosed = false; });
    /* the phone's keyboard: .eng-typing is on the window while it is up (see the top of this file) */
    var wasTyping = modal.classList.contains('eng-typing');
    new MutationObserver(function () {
      var now = modal.classList.contains('eng-typing');
      if (wasTyping && !now && document.activeElement === field) kbClosed = true;
      wasTyping = now;
      styleField();
    }).observe(modal, { attributes: true, attributeFilter: ['class'] });

    field.addEventListener('input', function () { setTimeout(paint, 0); });   /* after the theme has filled its grey box */
    field.addEventListener('change', paint);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.modal-toggle-size')) preload();
      if (e.target.closest && e.target.closest('.engravfontvariant li, .popvariant-picker, .modal-toggle-size')) {
        setTimeout(function () { pin(); paint(); }, 60);   /* after the theme has moved .active / cleared the field */
      }
    });
    if (document.fonts && document.fonts.load) {
      Promise.all([
        document.fonts.load("400 21px 'Novium Engrave'"),
        document.fonts.load("400 21px 'Novium Engrave Script'"),
        document.fonts.load("400 21px 'Novium Engrave'", '\u2665\u2661'),
        PEN === 'future' ? document.fonts.load("400 21px 'Novium Engrave Regular'") : null
      ]).then(function () { capCache = {}; paint(); }).catch(function () {});
    }
    paint();
    if (/[?&]nvmdebug=1\b/.test(location.search)) calibrate(svg, nodes, paint);
  }

  /* ========================================================================
     CALIBRATION TOOL  —  add ?nvmdebug=1 to the page URL, open the window,
     type a name. Same keys as the prototype's tool:
       drag the name   move it (capX / capY)
       R               switch between CARD and PEN (the active one is outlined)
       arrows          nudge 1 (shift = 5), even with the cursor in the field
       o / p           position along the pen
       n / m           letter height
       j / u           lift (above / below the barrel's axis)
       [ / ]           angle
       wheel on name   barrel size (letters and position follow)
     The panel bottom-left prints the two lines to send back; Copy puts them
     on the clipboard. Values are kept in THIS browser only (a reload keeps
     them, customers never see them); Reset goes back to the file's values.
     ======================================================================== */
  function calibrate(svg, nodes, paint) {
    /* one saved set per pose group; pick a colour in the window to edit its group */
    /* Interstellar keeps its 30 Sep keys (per card size); Future / Classic: per pen */
    function store(g) { return 'nvm-cal-' + (PEN === 'interstellar' ? SCALE : PEN) + '-' + g; }
    var DEFAULTS = {};
    var active = 'card';

    Object.keys(GROUPS).forEach(function (g) {
      DEFAULTS[g] = JSON.stringify(GROUPS[g].regions);
      try {
        var saved = JSON.parse(localStorage.getItem(store(g)) || 'null');
        if (saved) KEYS.forEach(function (k) { if (saved[k]) GROUPS[g].regions[k] = saved[k]; });
      } catch (e) {}
    });
    function save() { try { localStorage.setItem(store(CFG.calibrationFor), JSON.stringify(CFG.regions)); } catch (e) {} }

    var panel = document.createElement('div');
    panel.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;max-width:calc(100vw - 16px);' +
      'background:rgba(0,0,0,.85);color:#9f9;font:11px/1.45 Menlo,Consolas,monospace;padding:8px 10px;' +
      'border-radius:6px;white-space:pre-wrap;word-break:break-all;';
    var out = document.createElement('div');
    var bar = document.createElement('div');
    bar.style.cssText = 'margin-top:6px;display:flex;gap:6px;';
    function button(label, fn) {
      var b = document.createElement('button');
      b.type = 'button'; b.textContent = label;
      b.style.cssText = 'font:11px Menlo,monospace;padding:3px 8px;background:#9f9;color:#000;border:0;border-radius:3px;cursor:pointer;';
      b.addEventListener('click', fn);
      bar.appendChild(b);
      return b;
    }
    var copyBtn = button('Copy', function () {
      var text = lines();
      var done = function () { copyBtn.textContent = 'Copied'; setTimeout(function () { copyBtn.textContent = 'Copy'; }, 1200); };
      var fallback = function () {
        var ta = document.createElement('textarea');
        ta.value = text; ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
        document.body.appendChild(ta); ta.select();
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) {}
        ta.remove();
        if (ok) return done();
        /* clipboard blocked: select the lines so they can be copied by hand */
        var range = document.createRange(); range.selectNodeContents(out);
        var sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
        copyBtn.textContent = 'Selected - copy it';
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, fallback);
      else fallback();
    });
    button('Card / Pen', function () { active = active === 'card' ? 'shaft' : 'card'; redraw(); });
    button('Reset', function () {
      if (!confirm('Go back to the values in the file for "' + CFG.calibrationFor + '"?')) return;
      var d = JSON.parse(DEFAULTS[CFG.calibrationFor]);
      KEYS.forEach(function (k) { CFG.regions[k] = d[k]; });
      try { localStorage.removeItem(store(CFG.calibrationFor)); } catch (e) {}
      redraw();
    });
    panel.appendChild(out); panel.appendChild(bar);
    document.body.appendChild(panel);

    function fmt(k) {
      var r = CFG.regions[k];
      return k + ': { capX: ' + r.capX + ', capY: ' + r.capY + ', rotate: ' + r.rotate +
             ', barrelPx: ' + r.barrelPx + ', endGap: ' + r.endGap + ', height: ' + r.height +
             ', lift: ' + r.lift + (r.anchor ? ", anchor: '" + r.anchor + "'" : '') + ' }';
    }
    function lines() {
      var head = PEN === 'interstellar' ? CFG.calibrationFor + ', card at ' + SCALE + '%' : PEN + ' ' + CFG.calibrationFor;
      return '// ' + head + '\n' + fmt('card') + ',\n' + fmt('shaft');
    }

    function redraw() {
      paint();
      KEYS.forEach(function (k) {
        var t = nodes[k], on = k === active;
        set(t, 'pointer-events', 'all');
        set(t, 'cursor', 'move');
        set(t, 'stroke', on ? '#ff2d6f' : 'none');
        set(t, 'stroke-width', on ? (k === 'card' ? '1.2px' : '0.6px') : '0');
      });
      out.textContent = lines() + '\n\nediting: ' + (active === 'card' ? 'CARD' : 'PEN') +
        ' of group "' + CFG.calibrationFor + '"   (saved in this browser only)\n' +
        'pick another colour in the window to edit its group\n' +
        'drag = move · arrows = nudge · o/p = position · n/m = height\n' +
        'j/u = lift · [ ] = angle · wheel = barrel size · R = card/pen';
      save();
    }

    var drag = null;
    svg.addEventListener('pointerdown', function (e) {
      var t = e.target.closest && e.target.closest('text');
      if (!t) return;
      active = t === nodes.shaft ? 'shaft' : t === nodes.card ? 'card' : active;
      var r = CFG.regions[active];
      drag = { sx: e.clientX, sy: e.clientY, ox: r.capX, oy: r.capY };
      /* free the other keys (o/p, n/m...) too: preventDefault below can keep the focus in the field */
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      try { t.setPointerCapture(e.pointerId); } catch (err) {}
      e.preventDefault();
      redraw();
    });
    svg.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var scale = CFG.viewBox / svg.getBoundingClientRect().width;
      var r = CFG.regions[active];
      r.capX = +(drag.ox + (e.clientX - drag.sx) * scale).toFixed(1);
      r.capY = +(drag.oy + (e.clientY - drag.sy) * scale).toFixed(1);
      redraw();
    });
    svg.addEventListener('pointerup', function () { drag = null; });
    svg.addEventListener('pointercancel', function () { drag = null; });

    svg.addEventListener('wheel', function (e) {
      var t = e.target.closest && e.target.closest('text');
      if (!t) return;
      e.preventDefault();
      active = t === nodes.shaft ? 'shaft' : 'card';
      var r = CFG.regions[active];
      var step = active === 'card' ? 1 : 0.2;
      r.barrelPx = Math.max(2, +(r.barrelPx + (e.deltaY < 0 ? step : -step)).toFixed(1));
      redraw();
    }, { passive: false });

    document.addEventListener('keydown', function (e) {
      /* never react to typing in a field: "Josephine" alone contains o, p, n.
         Arrows are the exception: they always nudge, even with the cursor in the
         name field (6 Oct: clicking the name did not free them for Ludovic) */
      var tg = e.target;
      var k = e.key;
      var typing = tg && (tg.tagName === 'INPUT' || tg.tagName === 'TEXTAREA' || tg.isContentEditable);
      if (typing && k.indexOf('Arrow') !== 0) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var r = CFG.regions[active];
      var step = e.shiftKey ? 5 : 1;
      if (k === '[')                 r.rotate = +(r.rotate - 0.5).toFixed(1);
      else if (k === ']')            r.rotate = +(r.rotate + 0.5).toFixed(1);
      else if (k === 'ArrowLeft')    r.capX = +(r.capX - step).toFixed(1);
      else if (k === 'ArrowRight')   r.capX = +(r.capX + step).toFixed(1);
      else if (k === 'ArrowUp')      r.capY = +(r.capY - step).toFixed(1);
      else if (k === 'ArrowDown')    r.capY = +(r.capY + step).toFixed(1);
      else if (k === 'o' || k === 'O') r.endGap = +(r.endGap - 0.05).toFixed(2);
      else if (k === 'p' || k === 'P') r.endGap = +(r.endGap + 0.05).toFixed(2);
      else if (k === 'n' || k === 'N') r.height = +(r.height - 0.01).toFixed(3);
      else if (k === 'm' || k === 'M') r.height = +(r.height + 0.01).toFixed(3);
      else if (k === 'j' || k === 'J') r.lift = +(r.lift - 0.01).toFixed(3);
      else if (k === 'u' || k === 'U') r.lift = +(r.lift + 0.01).toFixed(3);
      else if (k === 'r' || k === 'R') active = active === 'card' ? 'shaft' : 'card';
      else return;
      e.preventDefault();
      redraw();
    });

    /* the theme repaints on input / font / colour changes; keep the outline */
    var field = document.querySelector('.enggravpopbanner') &&
                document.querySelector('.enggravpopbanner').closest('.modal').querySelector('.input_text');
    if (field) field.addEventListener('input', function () { setTimeout(redraw, 0); });
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.engravfontvariant li, .popvariant-picker')) setTimeout(redraw, 80);
    });
    CFG.onChange = redraw;
    redraw();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
